'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { DELIVERY_ADDRESS_COOKIE } from '../constants'
import type { DeliveryAddress } from '../entities'
import {
  mapCookieAddressDataToDeliveryAddress,
  mapDeliveryAddressToCookieAddress,
} from '../helpers'
import { deleteCookie, getCookie, setCookie } from '@/tools/cookies'
import { setRequestHeaders } from '@/tools/fetcher'
import { useErrorHandling } from '@/tools/o11y/client'
import { setUserAttributes } from '@/tools/toggles/service'

export type UserAddressContext = {
  deliveryAddress: DeliveryAddress | null
  setDeliveryAddress: (newAddress: DeliveryAddress | null) => Promise<void>
}

const DeliveryAddressContext = createContext<UserAddressContext>({
  deliveryAddress: null,
  setDeliveryAddress: () => Promise.resolve(),
})

export const DeliveryAddressProvider = ({ children }: { children: React.ReactNode }) => {
  const { execWithErrorTracking } = useErrorHandling()
  const [internalAddress, setInternalAddress] = useState<DeliveryAddress | null>(null)

  const setDeliveryAddress = async (address: DeliveryAddress | null) => {
    if (!address) {
      deleteCookie(DELIVERY_ADDRESS_COOKIE)
    } else {
      const cookieAddress = await mapDeliveryAddressToCookieAddress(address)
      const stringifiedCookieAddress = JSON.stringify(cookieAddress)
      setCookie(DELIVERY_ADDRESS_COOKIE, stringifiedCookieAddress)
      // TODO: Move this logic to LastBrowsedLocation Hook (when implemented)
      const normalizedCityCode = address.cityCode.toUpperCase()
      const normalizedCountryCode = address.countryCode.toUpperCase()
      setRequestHeaders('Glovo-Location-City-Code', normalizedCityCode)
      setRequestHeaders('Glovo-Location-Country-Code', normalizedCountryCode)
      setUserAttributes({ cityCode: normalizedCityCode, countryCode: normalizedCountryCode })
    }
    setInternalAddress(address)
  }

  /**
   *
   * Auto-initialize delivery address when Context is mounted, using the
   * address stored on the cookie.
   *
   */
  useEffect(() => {
    execWithErrorTracking(async () => {
      const cookieData = getCookie(DELIVERY_ADDRESS_COOKIE)
      const address = mapCookieAddressDataToDeliveryAddress(cookieData)
      await setDeliveryAddress(address)
    })
  }, [execWithErrorTracking])

  return (
    <DeliveryAddressContext.Provider
      value={{
        deliveryAddress: internalAddress,
        setDeliveryAddress,
      }}
    >
      {children}
    </DeliveryAddressContext.Provider>
  )
}

export const useDeliveryAddress = () => useContext(DeliveryAddressContext)
